/**
 * A hand of real cards, fanned.
 *
 * This is the artwork on the All Cards tile. Its three neighbours on that menu
 * carry real set and era logos pulled from our own storage, and the fourth was
 * a grey grid glyph, which read as the thinnest of the four doors when it is
 * actually the widest one.
 *
 * Built from live `<img>` elements under CSS transforms rather than one
 * pre-rendered picture, for three reasons that all matter here. It stays sharp
 * at any density, including the 3x phone this ships to. It takes the theme,
 * because the shadow and the card edge are drawn in CSS and not baked into
 * pixels. And when the catalogue eventually re-mirrors an image, the tile picks
 * up the new one instead of showing a stale copy nobody remembers baking.
 *
 * Every URL is our own Supabase storage. Nothing here reaches tcgdex or
 * Collectr, which is the whole reason the art is mirrored in the first place.
 */
import { useEffect, useState } from 'react';
import { getCardImages } from '../../services/cardData';

/**
 * The five cards, oldest on the left.
 *
 * Chosen to be the history of the game in one glance rather than five nice
 * pictures. Each one is a different decade, a different frame, and a different
 * Pokemon, so no two read as the same card at 40 pixels wide:
 *
 *   1999  Base Set Charizard        the card the whole hobby is named after,
 *                                   and the yellow border reads at any size.
 *   2005  EX Deoxys Rayquaza Star   the Gold Star era. Nothing else in the
 *                                   game looks like a gold star.
 *   2013  Legendary Treasures       full-art EX, the frame that ended the
 *         Mewtwo-EX                 yellow border era.
 *   2021  Evolving Skies            the defining modern chase card, and an
 *         Umbreon VMAX alt art      unmistakable silhouette even tiny.
 *   2024  Surging Sparks            the newest face of the game, and the one
 *         Pikachu ex                every child recognises first.
 *
 * Ids, not URLs. The image behind an id is content-hashed and can be re-mirrored
 * at any time; the id cannot.
 */
export const FAN_CARD_IDS = ['base1-4', 'ex8-107', 'bw11-54', 'swsh7-215', 'sv08-238'];

/**
 * Where each card sits, as a fraction of the box rather than in pixels, so the
 * fan is the same shape on a small phone and a tablet. The lift is an arc: the
 * outer cards ride a little lower, the way a real hand of cards does.
 */
const LAYOUT = [
  { x: -44, rotate: -18, lift: 5 },
  { x: -22, rotate: -9, lift: 1 },
  { x: 0, rotate: 0, lift: 0 },
  { x: 22, rotate: 9, lift: 1 },
  { x: 44, rotate: 18, lift: 5 },
];

/** One shared read, because every tile that wants the fan wants the same five. */
let cachedUrls = null;
let inFlight = null;

function loadFanImages() {
  if (cachedUrls) return Promise.resolve(cachedUrls);
  if (inFlight) return inFlight;
  inFlight = getCardImages(FAN_CARD_IDS)
    .then((map) => {
      cachedUrls = FAN_CARD_IDS.map((id) => map[id] || null);
      return cachedUrls;
    })
    .catch(() => {
      inFlight = null;
      return null;
    });
  return inFlight;
}

/**
 * @param {{ height?: string }} props  `height` is the card height inside the
 *   box, as a CSS length. The width follows from the 5:7 card shape.
 */
export default function CardFan({ height = '74%' }) {
  const [urls, setUrls] = useState(cachedUrls);
  const [failed, setFailed] = useState({});

  useEffect(() => {
    if (cachedUrls) return undefined;
    let alive = true;
    loadFanImages().then((next) => { if (alive && next) setUrls(next); });
    return () => { alive = false; };
  }, []);

  // Nothing rather than a placeholder. The tile is already the right size and
  // the right colour, so an empty box for one beat is quieter than a grey
  // rectangle that then swaps for a picture.
  if (!urls) return <div style={styles.box} />;

  return (
    <div style={styles.box}>
      {urls.map((url, i) => {
        if (!url || failed[i]) return null;
        const place = LAYOUT[i];
        return (
          <img
            key={FAN_CARD_IDS[i]}
            src={url}
            alt=""
            loading="lazy"
            draggable={false}
            onError={() => setFailed((prev) => ({ ...prev, [i]: true }))}
            style={{
              ...styles.card,
              height,
              zIndex: i + 1,
              transform: `translate(-50%, ${place.lift}%) translateX(${place.x}%) rotate(${place.rotate}deg)`,
            }}
          />
        );
      })}
    </div>
  );
}

const styles = {
  box: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  card: {
    position: 'absolute',
    left: '50%',
    // The pivot sits well up from the floor of the box on purpose. Rotating a
    // card about its bottom edge swings the outer corner DOWN by half the card
    // width times the sine of the angle, so a fan pinned near the bottom has
    // its two outer cards clipped off by the tile's overflow. This clears it.
    bottom: '17%',
    // The 5:7 card shape, so only the height is ever set.
    aspectRatio: '5 / 7',
    width: 'auto',
    objectFit: 'contain',
    // Transform origin at the bottom of the card is what makes this a fan
    // rather than five tilted rectangles: the cards pivot where a hand holds
    // them.
    transformOrigin: '50% 100%',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.45)',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
};
